import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DynamicsSettings } from 'app/model/dataflow/dataset-settings/dynamics-settings';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { BooleanDataFlowType, IntegerDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { TabularTypeAdapter } from 'app/model/adapters/dataflow/formatAdapters/tabular-type.adapter';
import { DataFlowAdapterUtils } from 'app/model/adapters/dataflow/adapter.util';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(DynamicsSettings)
})
export class DynamicsAdapter extends TabularTypeAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, _linkedService: LinkedService | undefined, props: string[], _isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`store: '${DatasetFormatType.Dynamics}'`);
        props.push(`format: 'dynamicsformat'`);

        const entity = getAttributeValueFromDataset(dataset, 'tableName');
        if (entity) {
            props.push(`entity: ${DataFlowAdapterUtils.formatExpressionValue(entity)}`);
        }
    }

    public async toSourceDSL(setting: DynamicsSettings): Promise<string[]> {
        const props = [];
        props.push(`store: '${DatasetFormatType.Dynamics}'`);
        props.push(`format: 'dynamicsformat'`);
        if (setting.tableName.expression) {
            props.push(`entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        }
        if (setting.timeout.expression) {
            props.push(`timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.timeout, IntegerDataFlowType.TYPE)}`);
        }
        if (setting.useQuery && setting.query.expression) {
            props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(setting.query)}`);
        }
        if (setting.sinkImportSchema) {
            props.push(`sinkImportSchema: ${setting.sinkImportSchema}`);
        }
        return props;
    }

    public async toSourceModel(owner: DataFlowSource, configs: Map<string, string>, handledConfigs: string[]): Promise<DynamicsSettings> {
        const setting = await super.toSourceModel(owner, configs, handledConfigs) as DynamicsSettings;
        if (configs.has('entity')) {
            setting.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entity'), owner);
            handledConfigs.push('entity');
        }
        if (configs.has('timeout')) {
            setting.timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('timeout'), owner, IntegerDataFlowType.TYPE);
            handledConfigs.push('timeout');
        }
        return setting;
    }

    public async toSinkDSL(setting: DynamicsSettings): Promise<string[]> {
        const props = [];
        props.push(`store: '${DatasetFormatType.Dynamics}'`);
        props.push(`format: 'dynamicsformat'`);
        if (setting.tableName.expression) {
            props.push(`entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        }
        if (setting.timeout.expression) {
            props.push(`timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.timeout, IntegerDataFlowType.TYPE)}`);
        }
        props.push(`deletable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.allowDelete, BooleanDataFlowType.TYPE)}`);
        props.push(`insertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.allowInsert, BooleanDataFlowType.TYPE)}`);
        props.push(`updateable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.allowUpdate, BooleanDataFlowType.TYPE)}`);
        props.push(`upsertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.allowUpsert, BooleanDataFlowType.TYPE)}`);
        if (isTruthy(setting.allowDelete) || isTruthy(setting.allowUpsert) || isTruthy(setting.allowUpdate)) {
            if (setting.alternateKeyName.expression) {
                props.push(`alternateKeyName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.alternateKeyName)}`);
            }
        }
        return props;
    }

    public async toSinkModel(owner: DataFlowSink, configs: Map<string, string>, handledConfigs: string[]): Promise<DynamicsSettings> {
        const setting = await super.toSinkModel(owner, configs, handledConfigs) as DynamicsSettings;
        if (configs.has('entity')) {
            setting.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entity'), owner);
            handledConfigs.push('entity');
        }
        if (configs.has('timeout')) {
            setting.timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('timeout'), owner, IntegerDataFlowType.TYPE);
            handledConfigs.push('timeout');
        }
        if (configs.has('alternateKeyName')) {
            setting.alternateKeyName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('alternateKeyName'), owner);
            handledConfigs.push('alternateKeyName');
        }
        return setting;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): DynamicsSettings {
        const setting = Owned.create<DynamicsSettings>(DynamicsSettings.MODEL, owner);
        setting.initialize(undefined);
        return setting;
    }
}
